/**
 * Web twin of `CreateCvOptionCard`: one selectable option row in the create-CV
 * flow that expands inline while selected. Selection is a check rather than a
 * filled block, so a list of four reads as a menu instead of four competing
 * buttons.
 *
 * Same props as the native card on purpose - the two step components are
 * written once against this shape.
 */
import type { ReactNode } from 'react';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import { OPTION_ICON } from './create-cv-web-styles';

interface Props {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  selected: boolean;
  onPress: () => void;
  disabled?: boolean;
  /** Rendered under the row while selected (JD box, resume composer, …). */
  children?: ReactNode;
  /**
   * Replaces the trailing check. Rows that open a picker use it for a chevron:
   * the chosen value is already in the subtitle, so a check would be a second
   * glyph saying the same thing while the chevron says something new.
   */
  trailing?: ReactNode;
  accessibilityHint?: string;
}

export function CreateCvOptionCard({
  icon,
  title,
  subtitle,
  selected,
  onPress,
  disabled,
  children,
  trailing,
  accessibilityHint,
}: Props) {
  return (
    <div
      style={{
        borderRadius: 20,
        backgroundColor: selected ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.04)',
        opacity: disabled ? 0.45 : 1,
        overflow: 'hidden',
        transition: 'background-color 150ms',
      }}
    >
      <button
        type="button"
        onClick={onPress}
        disabled={disabled}
        aria-pressed={selected}
        title={accessibilityHint}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: '14px 18px',
          border: 0,
          background: 'transparent',
          cursor: disabled ? 'default' : 'pointer',
          textAlign: 'start' as const,
          fontFamily: 'inherit',
          width: '100%',
        }}
      >
        <span style={OPTION_ICON}>{icon}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            style={{
              display: 'block',
              fontSize: 15,
              fontWeight: 500,
              color: 'var(--text-primary)',
              lineHeight: '20px',
            }}
          >
            {title}
          </span>
          {subtitle ? (
            <span
              style={{
                display: 'block',
                fontSize: 13,
                lineHeight: '18px',
                color: 'var(--text-secondary)',
                marginTop: 2,
              }}
              dir="auto"
            >
              {subtitle}
            </span>
          ) : null}
        </span>
        {trailing ??
          (selected ? (
            <MaterialSymbol
              name="check_circle"
              size={20}
              style={{ color: 'var(--text-primary)', flexShrink: 0 }}
            />
          ) : null)}
      </button>
      {selected && children ? (
        <div
          style={{
            padding: '12px 18px 16px',
            borderTop: '1px solid var(--border-subtle, rgba(0,0,0,0.08))',
          }}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}
